import { PaginationConfig } from 'antd/lib/pagination'
import { FilterValue, TablePaginationConfig } from 'antd/lib/table/interface'
import isEqual from 'lodash.isequal'
import * as React from 'react'

import { useLocalStorage } from '@/hooks/useLocalStorage'
import { mergePropertiesWhenDefined } from '@/utils/commons'

export const INITIAL_DEFAULT_PAGINATION: PaginationConfig = {
  current: 1,
  pageSize: 10,
  pageSizeOptions: ['10', '25', '50'],
  showSizeChanger: true,
}

export type PaginatedComponentState = {
  componentId: string
  filters?: Record<string, FilterValue | null>
  pagination: PaginationConfig
  search?: string
  sort?: string[]
  roles?: ['LEAD', 'DEVELOPER']
}

export type PaginatedActionType =
  | 'SET_STATE'
  | 'SET_FILTERS'
  | 'SET_PAGINATION'
  | 'SET_SEARCH'
  | 'SET_SORT'

export type Action<ActionType> = {
  type: ActionType
  payload: any
}

export type PaginatedActionDispachers = {
  setFilters: (payload: object) => void
  setPaginationConfig: (payload: TablePaginationConfig) => void
  setSearchTerm: (payload: string) => void
  setSort: (payload: string[] | string) => void
  setStatus: (payload: string[] | string) => void
  setState: (payload: object) => void
}

export function getPaginatedComponentStateReducer<
  ActionType extends PaginatedActionType,
  StateType extends PaginatedComponentState,
>(
  storeOnLocalStorage?: (state: StateType) => void
): React.Reducer<StateType, Action<ActionType>> {
  return appendStoreOnLocalStorage(
    paginatedComponentStateReducer,
    storeOnLocalStorage
  )
}

export function paginatedComponentStateReducer<
  ActionType extends PaginatedActionType,
  StateType extends PaginatedComponentState,
>(prevState: StateType, action: Action<ActionType>): StateType {
  let nextState = Object.assign({}, prevState)
  const { type, payload } = action
  switch (type) {
    case 'SET_FILTERS': {
      if (!isEqual(prevState.filters, payload)) {
        resetPaginationCurrentPage(prevState, nextState)
        nextState.filters = Object.assign({}, prevState.filters, payload)
      }
      break
    }
    case 'SET_PAGINATION': {
      if (!isEqual(prevState.pagination, payload)) {
        nextState.pagination = Object.assign({}, prevState.pagination, payload)
        if (nextState.pagination?.current === prevState.pagination?.current) {
          nextState.pagination.current = 1
        }
      }
      break
    }
    case 'SET_SEARCH': {
      resetPaginationCurrentPage(prevState, nextState)
      nextState.search = payload
      break
    }
    case 'SET_SORT': {
      if (!isEqual(prevState.sort, payload)) {
        resetPaginationCurrentPage(prevState, nextState)
        nextState.sort = payload
      }
      break
    }
    case 'SET_STATE': {
      nextState = Object.assign(nextState, {
        ...payload,
      })
      break
    }
  }
  return nextState
}

export const concatReducers =
  <T, A extends Action<any>>(...reducers: Array<React.Reducer<any, any>>) =>
  (state: T | undefined, action: A): T =>
    reducers
      .filter((reducer) => !!reducer)
      .reduce(
        (accumulatedState, reducer) => reducer(accumulatedState, action),
        state
      )

export function getPaginatedActionDispatchers(
  dispatch
): PaginatedActionDispachers {
  return {
    setFilters: (payload: object) => dispatch({ type: 'SET_FILTERS', payload }),
    setPaginationConfig: (payload: TablePaginationConfig) =>
      dispatch({ type: 'SET_PAGINATION', payload }),
    setSearchTerm: (payload: string) =>
      dispatch({ type: 'SET_SEARCH', payload }),
    setSort: (payload: string[] | string) =>
      dispatch({ type: 'SET_SORT', payload }),
    setStatus: (payload: string[] | string) =>
      dispatch({ type: 'SET_STATUS', payload }),
    setState: (payload: object) => dispatch({ type: 'SET_STATE', payload }),
  }
}

function resetPaginationCurrentPage(
  state: PaginatedComponentState,
  newState: PaginatedComponentState
) {
  return (newState.pagination = {
    ...state.pagination,
    current: 1,
  })
}

export function useReducerWithLocalStorage<ActionType, StateType>({
  componentId,
  currentState,
  initialState,
  reducer,
  workspaceId,
}: {
  componentId: string
  currentState?: StateType // will be used to override the initialState or localStorage state
  initialState?: StateType // initialState is the default when no localStorage state is found
  reducer: React.Reducer<any, any>
  workspaceId: string
}): {
  dispatch: React.Dispatch<Action<ActionType>>
  state: StateType
} {
  const [localStorageCopy, storeOnLocalStorage] = useLocalStorage<StateType>(
    componentId,
    workspaceId,
    initialState
  )
  const initialization = mergePropertiesWhenDefined<StateType>(
    currentState,
    localStorageCopy
  )
  const [state, dispatch] = React.useReducer<
    React.Reducer<StateType, Action<ActionType>>
  >(appendStoreOnLocalStorage(reducer, storeOnLocalStorage), initialization)

  return {
    dispatch,
    state,
  }
}

const constrainPagination = <T extends PaginatedComponentState>(
  state: T,
  pageSizeOptions: Array<string | number>
): T => {
  const options = pageSizeOptions.map((option: string | number) => {
    return parseInt(option.toString())
  })

  const validPageSize = options.includes(state.pagination.pageSize)
    ? state.pagination.pageSize
    : options[0]

  return {
    ...state,
    pagination: {
      ...state.pagination,
      pageSize: validPageSize,
      pageSizeOptions: pageSizeOptions,
    },
  }
}

type PaginatedReducerWithLocalStorageOptions<
  StateType extends PaginatedComponentState,
> = {
  componentId: string
  workspaceId: string
  getStateReducer?: (callback: (state: any) => void) => React.Reducer<any, any>
  initialState?: StateType
}

type PaginatedReducerWithLocalStorage<
  ActionType extends string | PaginatedActionType,
  StateType extends PaginatedComponentState,
> = {
  dispatch: React.Dispatch<Action<ActionType>>
  dispatchers: PaginatedActionDispachers
  pagination: {
    page: number
    size: number
  }
  state: StateType
}

// TODO: Refactor to generalize this function and reuse useReducerWithLocalStorage
export function usePaginatedReducerWithLocalStorage<
  ActionType extends string | PaginatedActionType,
  StateType extends PaginatedComponentState,
>({
  workspaceId,
  componentId,
  getStateReducer,
  initialState,
}: PaginatedReducerWithLocalStorageOptions<StateType>): PaginatedReducerWithLocalStorage<
  ActionType,
  StateType
> {
  const defaultPageSizeOptions = initialState
    ? initialState.pagination.pageSizeOptions
    : INITIAL_DEFAULT_PAGINATION.pageSizeOptions

  const [localStorageCopy, storeOnLocalStorage] = useLocalStorage<StateType>(
    componentId,
    workspaceId,
    (defaultState: StateType): StateType => {
      return defaultState?.componentId !== componentId
        ? initialState ||
            ({
              componentId,
              pagination: INITIAL_DEFAULT_PAGINATION, // NOTE (BC) currently the page size options here are ignored when displaying.
            } as StateType)
        : defaultState
    }
  )

  const [state, dispatch] = React.useReducer<
    React.Reducer<StateType, Action<ActionType>>
  >(
    concatReducers(
      getStateReducer && getStateReducer(storeOnLocalStorage),
      getPaginatedComponentStateReducer(storeOnLocalStorage)
    ),
    constrainPagination(localStorageCopy, defaultPageSizeOptions)
  )

  const dispatchers = getPaginatedActionDispatchers(dispatch)

  const pagination = convertStatePaginationToAnt(state)

  return {
    dispatch,
    dispatchers,
    pagination,
    state,
  }
}

function appendStoreOnLocalStorage(
  reducer: React.Reducer<any, any>,
  storeOnLocalStorage: (state: any) => void
) {
  return (state: any, action: any) => {
    const newState = reducer(state, action)
    storeOnLocalStorage(newState)
    return newState
  }
}

export function convertStatePaginationToAnt(state: PaginatedComponentState) {
  const page = state.pagination?.current > 0 ? state.pagination?.current - 1 : 0
  return (
    state?.pagination && {
      page,
      size: state.pagination?.pageSize,
    }
  )
}
